import React, { useEffect, useMemo, useState } from "react";
import SearchableSelect from "./SearchableSelect";

const ROLE_OPTIONS = [
  { key: "sakha_id", label: "Self" },
  { key: "dadisa_sakha_id", label: "Dadisa" },
  { key: "nanisa_sakha_id", label: "Nanisa" },
  { key: "mamasa_sakha_id", label: "Mamasa" },
];

const C = {
  card: "#fffdfb",
  border: "rgba(132,42,45,0.12)",
  borderStrong: "rgba(132,42,45,0.18)",
  accent: "#842A2D",
  accentDark: "#6f2327",
  accentLight: "#fbefea",
  text: "#2f2833",
  muted: "#6f6670",
  mutedSoft: "#93808a",
  soft: "#fff8f4",
  softMuted: "#ecd2cb",
};

export default function SakhaAssignmentsField({
  options = [],
  values,
  onChange,
  onCreateOption = null,
  creatingOption = false,
  disabled = false,
}) {
  const normalizedOptions = useMemo(() => (Array.isArray(options) ? options : []), [options]);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedSakhaId, setSelectedSakhaId] = useState("");

  const optionMap = useMemo(() => {
    const map = new Map();
    normalizedOptions.forEach((option) => {
      map.set(String(option.id), option.label);
    });
    return map;
  }, [normalizedOptions]);

  const assignments = useMemo(
    () =>
      ROLE_OPTIONS.map((role) => ({
        ...role,
        value: String(values?.[role.key] || ""),
        sakhaName: optionMap.get(String(values?.[role.key] || "")) || "",
      })).filter((role) => role.value && role.sakhaName),
    [optionMap, values]
  );
  const selectedRoleOptions = useMemo(() => {
    const assignedRoleKeys = new Set(assignments.map((assignment) => assignment.key));
    return ROLE_OPTIONS
      .filter((role) => !assignedRoleKeys.has(role.key) || role.key === selectedRole)
      .map((role) => ({ id: role.key, label: role.label }));
  }, [assignments, selectedRole]);

  const currentAssignment = selectedRole ? String(values?.[selectedRole] || "") : "";
  const actionLabel = currentAssignment ? "Update selection" : "Add selection";
  const emptyStateMessage = !normalizedOptions.length
    ? "No sakha assignments added yet."
    : disabled
      ? "Sakha assignments are read-only right now."
      : "No sakha assignments added yet.";
  const sakhaSelectDisabled = disabled || (!normalizedOptions.length && typeof onCreateOption !== "function");

  useEffect(() => {
    if (!selectedSakhaId) return;
    if (!optionMap.has(String(selectedSakhaId))) {
      setSelectedSakhaId("");
    }
  }, [optionMap, selectedSakhaId]);

  const applySelection = () => {
    if (!selectedRole || !selectedSakhaId) return;
    onChange?.(selectedRole, String(selectedSakhaId));
    setSelectedRole("");
    setSelectedSakhaId("");
  };

  const clearSelection = (roleKey) => {
    onChange?.(roleKey, "");
    if (selectedRole === roleKey) {
      setSelectedRole("");
      setSelectedSakhaId("");
    }
  };

  const editSelection = (roleKey, sakhaId) => {
    setSelectedRole(roleKey);
    setSelectedSakhaId(String(sakhaId || ""));
  };

  return (
    <div style={{ ...styles.wrap, ...(disabled ? styles.wrapDisabled : {}) }}>
      <div style={styles.toolbar}>
        <SearchableSelect
          value={selectedRole}
          options={selectedRoleOptions}
          onChange={setSelectedRole}
          placeholder="Select relation"
          disabled={disabled}
          fieldShellStyle={styles.select}
          fieldShellDisabledStyle={styles.selectDisabled}
          inputStyle={styles.selectInput}
          chevronButtonStyle={styles.selectChevronButton}
          menuStyle={styles.selectMenu}
          optionStyle={styles.selectOption}
          optionActiveStyle={styles.selectOptionActive}
        />

        <SearchableSelect
          value={selectedSakhaId}
          options={normalizedOptions}
          onChange={setSelectedSakhaId}
          placeholder={normalizedOptions.length ? "Select sakha" : "Type sakha name"}
          disabled={sakhaSelectDisabled}
          onEmptyAction={
            typeof onCreateOption === "function"
              ? async (query) => {
                  const createdId = await onCreateOption(query);
                  if (createdId) setSelectedSakhaId(String(createdId));
                }
              : null
          }
          emptyActionLabel={(query) =>
            creatingOption ? "Adding sakha..." : `Add "${query}" as new sakha`
          }
          emptyActionHint="This new sakha will be added under the currently selected caste."
          emptyActionDisabled={creatingOption}
          fieldShellStyle={styles.select}
          fieldShellDisabledStyle={styles.selectDisabled}
          inputStyle={styles.selectInput}
          chevronButtonStyle={styles.selectChevronButton}
          menuStyle={styles.selectMenu}
          optionStyle={styles.selectOption}
          optionActiveStyle={styles.selectOptionActive}
        />

        <button
          type="button"
          style={{
            ...styles.actionBtn,
            ...((disabled || !selectedRole || !selectedSakhaId) ? styles.actionBtnDisabled : {}),
          }}
          onClick={applySelection}
          disabled={disabled || !selectedRole || !selectedSakhaId}
        >
          {actionLabel}
        </button>
      </div>

      {assignments.length > 0 ? (
        <div style={styles.assignmentList}>
          {assignments.map((assignment) => (
            <div key={assignment.key} style={styles.assignmentCard}>
              <div>
                <div style={styles.assignmentRole}>{assignment.label}</div>
                <div style={styles.assignmentName}>{assignment.sakhaName}</div>
              </div>

              <div style={styles.assignmentActions}>
                <button
                  type="button"
                  style={styles.secondaryBtn}
                  onClick={() => editSelection(assignment.key, assignment.value)}
                  disabled={disabled}
                >
                  Edit
                </button>
                <button
                  type="button"
                  style={styles.clearBtn}
                  onClick={() => clearSelection(assignment.key)}
                  disabled={disabled}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.emptyState}>{emptyStateMessage}</div>
      )}
    </div>
  );
}

const styles = {
  wrap: {
    padding: 0,
  },
  wrapDisabled: {
    opacity: 0.82,
  },
  toolbar: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    alignItems: "center",
    marginBottom: 12,
  },
  select: {
    width: "100%",
    minHeight: 46,
    borderRadius: 14,
    border: `1px solid ${C.borderStrong}`,
    background: "#fffcfb",
    fontSize: 14,
    color: C.text,
    outline: "none",
    boxSizing: "border-box",
  },
  selectDisabled: {
    background: C.soft,
    color: C.mutedSoft,
  },
  selectInput: {
    minHeight: 46,
    padding: "0 12px",
    fontSize: 13,
    fontWeight: 600,
    color: C.text,
  },
  selectChevronButton: {
    height: 46,
    color: C.muted,
  },
  selectMenu: {
    borderRadius: 20,
    border: `1px solid ${C.borderStrong}`,
    background: C.card,
    boxShadow: "0 18px 36px rgba(76,44,51,0.10)",
  },
  selectOption: {
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    color: C.text,
    fontSize: 14,
  },
  selectOptionActive: {
    borderColor: C.accent,
    background: C.accentLight,
    color: C.accentDark,
  },
  actionBtn: {
    width: "100%",
    minHeight: 46,
    border: "none",
    borderRadius: 14,
    background: "linear-gradient(145deg, #842A2D, #6f2327)",
    color: C.card,
    fontWeight: 700,
    padding: "0 16px",
    cursor: "pointer",
    boxShadow: "0 14px 26px rgba(132,42,45,0.18)",
  },
  actionBtnDisabled: {
    background: C.softMuted,
    cursor: "not-allowed",
  },
  assignmentList: {
    display: "grid",
    gap: 8,
  },
  assignmentCard: {
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    background: "rgba(255,255,255,0.72)",
    padding: "12px 14px",
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
  },
  assignmentRole: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    color: C.accentDark,
    marginBottom: 2,
  },
  assignmentName: {
    fontSize: 14,
    fontWeight: 700,
    color: C.text,
  },
  assignmentActions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  secondaryBtn: {
    minHeight: 36,
    borderRadius: 10,
    border: `1px solid ${C.borderStrong}`,
    background: "#fff4f1",
    color: C.accentDark,
    padding: "0 12px",
    fontWeight: 700,
    cursor: "pointer",
  },
  clearBtn: {
    minHeight: 36,
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    background: C.card,
    color: C.muted,
    padding: "0 12px",
    fontWeight: 700,
    cursor: "pointer",
  },
  emptyState: {
    border: `1px dashed ${C.borderStrong}`,
    borderRadius: 16,
    background: "rgba(255,248,244,0.6)",
    color: C.muted,
    padding: "12px 14px",
    fontSize: 13,
    lineHeight: 1.6,
  },
};
